import { useContext } from "react";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import {
  ErrorDialogContext,
  errorHandledAsync,
  errorHandledSync,
} from "@/shared/context/error-context";
import { useTradesContext, Detail, TradesHistory } from "@/features/accounts/shared/contexts/trades-context";
import { useGetFinanceTrades } from "@/features/accounts/finance/hooks/use-get-trades";
import { useGetFinanceTradesHistory } from "@/features/accounts/finance/hooks/use-get-trades-history";
import { useGetCrecaTrades } from "@/features/accounts/creca/hooks/use-gate-trades";
import { useGetCrecaTradesHistory } from "@/features/accounts/creca/hooks/use-get-trades-history";
import { useUpdateFinanceTrades } from "@/features/accounts/finance/hooks/use-update-trades"
import { useUpdateCrecaTrades } from "@/features/accounts/creca/hooks/use-update-trades"
import { useUpdatePaymentScrapTrades } from "@/features/accounts/payment/scraping/hooks/use-update-trades";
import { useUpdatePaymentApiTrades } from "@/features/accounts/payment/api/hooks/use-update-trades";
import { useGetPaymentTrades } from "@/features/accounts/payment/scraping/hooks/use-get-trades";
import { useGetPaymentTradesHistory } from "@/features/accounts/payment/scraping/hooks/use-get-trades-history";
import { useSynchronizePaymentTrades } from "@/features/accounts/payment/scraping/hooks/use-synchronize-trades";
import { useUpdateEcshopTrades } from "@/features/accounts/ecshop/hooks/use-update-trades";
import { useGetEcshopTrades } from "@/features/accounts/ecshop/hooks/use-get-trades";
import { useGetEcshopTradesHistory } from "@/features/accounts/ecshop/hooks/use-get-trades-history";
import { useGetCsvExcelTrades } from "@/features/accounts/csv-excel/hooks/use-get-trades";
import { useGetCsvExcelTradesHistory } from "@/features/accounts/csv-excel/hooks/use-get-trades-history";
import { useGetOcrTrades } from "@/features/accounts/ocr/hooks/use-get-trades";
import { useGetOcrTradesHistory } from "@/features/accounts/ocr/hooks/use-get-trades-history";
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { useUpdateFinanceTradesAdditions } from "@/features/accounts/finance/hooks/use-update-trades-additions"
import { useUpdateCrecaTradesAdditions } from "@/features/accounts/creca/hooks/use-update-trades-additions"
import { useUpdatePaymentTradesAdditions } from "@/features/accounts/payment/scraping/hooks/use-update-trades-additions"
import { useUpdateEcshopTradesAdditions } from "@/features/accounts/ecshop/hooks/use-update-trades-additions"
import { useUpdateCsvExcelTradesAdditions } from "@/features/accounts/csv-excel/hooks/use-update-trades-additions"
import { useUpdateOcrTradesAdditions } from "@/features/accounts/ocr/hooks/use-update-trades-additions";
import { useAccountPollingContext } from "@/features/accounts/shared/contexts/account-polling-context";
import { useOneTimePassContext } from "@/features/accounts/payment/scraping/contexts/one-time-pass-context";
import { useGetScrapFinanceTrades } from "@/features/accounts/scrap-finance/hooks/use-get-trades";
import { useGetScrapFinanceTradesHistory } from "@/features/accounts/scrap-finance/hooks/use-get-trades-history";
import { useUpdateScrapFinanceTradesAdditions } from "@/features/accounts/scrap-finance/hooks/use-update-trades-additions";
import { utilities } from "@/shared/utils/utility";


export const useViewTrades = () => {
    const {
      selectedAccount,
    } = useAccountGroupsContext();
  const {
    details,
    setDetails,
    setSelectedDetails,
    setTradesStartDate,
    setTradesEndDate,
    sortType,
    searchTradeType,
    searchStartDate,
    searchEndDate,
    searchMinAmount,
    searchMaxAmount,
    searchMinBalance,
    searchMaxBalance,
    searchSummaryKeyword,
    searchMemoKeyword,
    forceTradesSetDone,
  } = useTradesContext();
  const {
    addPollingInfo,
  } = useAccountPollingContext();
  const {
    sessionKey,
    otpInfo,
  } = useOneTimePassContext();
  const { updateFinanceTrades } = useUpdateFinanceTrades();
  const { getFinanceTrades } = useGetFinanceTrades();
  const { getFinanceTradesHistory } = useGetFinanceTradesHistory();
  const { updateCrecaTrades } = useUpdateCrecaTrades();
  const { getCrecaTrades } = useGetCrecaTrades();
  const { getCrecaTradesHistory } = useGetCrecaTradesHistory();
  const { updatePaymentScrapTrades } = useUpdatePaymentScrapTrades();
  const { updatePaymentApiTrades } = useUpdatePaymentApiTrades();
  const { getPaymentTrades } = useGetPaymentTrades();
  const { getPaymentTradesHistory } = useGetPaymentTradesHistory();
  const { synchronizePaymentTrades } = useSynchronizePaymentTrades();
  const { updateEcshopTrades } = useUpdateEcshopTrades();
  const { getEcshopTrades } = useGetEcshopTrades();
  const { getEcshopTradesHistory } = useGetEcshopTradesHistory();
  const { getCsvExcelTrades } = useGetCsvExcelTrades();
  const { getCsvExcelTradesHistory } = useGetCsvExcelTradesHistory();
  const { getOcrTrades } = useGetOcrTrades();
  const { getOcrTradesHistory } = useGetOcrTradesHistory();
  const { updateFinanceTradesAdditions } = useUpdateFinanceTradesAdditions();
  const { updateCrecaTradesAdditions } = useUpdateCrecaTradesAdditions();
  const { updatePaymentTradesAdditions } = useUpdatePaymentTradesAdditions();
  const { updateEcshopTradesAdditions } = useUpdateEcshopTradesAdditions();
  const { updateCsvExcelTradesAdditions } = useUpdateCsvExcelTradesAdditions();
  const { updateOcrTradesAdditions } = useUpdateOcrTradesAdditions();
  const { getScrapFinanceTrades } = useGetScrapFinanceTrades();
  const { updateScrapFinanceTradesAdditions } = useUpdateScrapFinanceTradesAdditions();
  const { getScrapFinanceTradesHistory } = useGetScrapFinanceTradesHistory();
  const { toDate, toYYYYMMDD, addYears, getNextDay, getPreviousDay } = utilities();

  // ローディング用コンテキスト
  const loadingCtx = useContext(LoadingModalContext);
  // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);

  const addOrdersHeadersToList = (details: Detail[]): Detail[] => {
    let preOrderId = ""
    let preDate = ""
    let preValidate = true
    let preCancel = false
    let cancel = false
    let sumDeposit = 0
    let sumWithdraw = 0
    let tradeIds = ""
    let orderTmpList: Detail[] = []
    const ecshopDetails = details.reduce((acc: Detail[], v, index): Detail[] => {
      cancel = v.cancelOrder === 1 ? true : false

      // 受注IDが変わったためヘッダを作成して受注詳細リストとともに格納
      if (preOrderId !== "" && preOrderId !== v.orderId) {
        acc.push({
          mlinkAccountId: v.mlinkAccountId,
          mlinkId: v.mlinkId,
          tradeId: tradeIds,
          date: preDate,
          sortOrder: 0, // 通常受注ヘッダ:0、通常受注細部:1-4、キャンセル受注ヘッダ:5、キャンセル受注詳細:6-9
          tradeType: 1, // 通常のヘッダのため
          amount: sumDeposit, // 通常のヘッダのため
          balance: 0,
          summary: `売上ID ${preOrderId}`,
          memo: "",
          invalid: preValidate ? 0 : 1,
          orderId: preOrderId,
          cancelOrder: 0, // 通常のヘッダのため
          isHeader: 1,
          isEcshopTrade: 1,
          ocrProcessId: "",
        })
        acc.push(...orderTmpList)
        sumDeposit = 0
        sumWithdraw = 0
        tradeIds = ""
        orderTmpList = []
      }

      // キャンセル => 通常受注に変わったためヘッダを作成して受注リストとともに格納
      if (preCancel && !cancel && preOrderId === v.orderId) {
        acc.push({
          mlinkAccountId: v.mlinkAccountId,
          mlinkId: v.mlinkId,
          tradeId: tradeIds,
          date: preDate,
          sortOrder: 5, // 通常受注ヘッダ:0、通常受注細部:1-4、キャンセル受注ヘッダ:5、キャンセル受注詳細:6-9
          tradeType: 2, // キャンセルのヘッダのため
          amount: sumWithdraw, // キャンセルのヘッダのため
          balance: 0,
          summary: `売上ID ${preOrderId}`,
          memo: "",
          invalid: preValidate ? 0 : 1,
          orderId: preOrderId,
          cancelOrder: 1, // キャンセルのヘッダのため
          isHeader: 1,
          isEcshopTrade: 1,
          ocrProcessId: "",
        })
        acc.push(...orderTmpList)
        sumDeposit = 0
        sumWithdraw = 0
        tradeIds = ""
        orderTmpList = []
      }

      orderTmpList.unshift(v)
      preOrderId = v.orderId
      preDate = v.date
      preValidate = v.invalid === 1 ? false : true
      preCancel = cancel
      tradeIds += v.tradeId
      if (v.tradeType === 1) {
        sumDeposit += v.amount
      } else if (v.tradeType === 2) {
        sumWithdraw += v.amount
      }

      // 最後の要素の場合ヘッダを作成して受注リストとともに格納
      if (index === details.length - 1) {
        acc.push({
          mlinkAccountId: v.mlinkAccountId,
          mlinkId: v.mlinkId,
          tradeId: tradeIds,
          date: preDate,
          sortOrder: 0,
          tradeType: cancel ? 2 : 1,
          amount: cancel ? sumWithdraw : sumDeposit,
          balance: 0,
          summary: `売上ID ${preOrderId}`,
          memo: "",
          invalid: preValidate ? 0 : 1,
          orderId: preOrderId,
          cancelOrder: cancel ? 1 : 0,
          isHeader: 1,
          isEcshopTrade: 1,
          ocrProcessId: "",
        })
        acc.push(...orderTmpList)
      }
      return acc;
    },[]);
    return ecshopDetails;
  };

  // ECショップの金額の条件検索用受注IDリスト取得
  const amountMatchOrderIdList = (details: Detail[]): string[] => {
    // 受注単位（ヘッダー）の値で行う
    const list = details
      .filter(item => item.isHeader === 1)
      // 入出金金額
      .filter(item => (searchMinAmount !== undefined ? item.amount >= searchMinAmount : true))
      .filter(item => (searchMaxAmount !== undefined ? item.amount <= searchMaxAmount : true))
    return list.map(item => {return item.orderId});
  };

  // ECショップの摘要・メモの条件検索用受注IDリスト取得
  // 受注(ヘッダ+明細)単位で判定する。取引内容・メモのそれぞれについて、
  // その受注内のいずれかの行にキーワードが含まれていれば該当とみなす。
  const keywordMatchOrderIdList = (details: Detail[]): string[] => {
    const orderIdToDetails = details.reduce((acc, item) => {
      if (!acc.has(item.orderId)) {
        acc.set(item.orderId, []);
      }
      acc.get(item.orderId)!.push(item);
      return acc;
    }, new Map<string, Detail[]>());

    const matchedOrderIds: string[] = [];
    orderIdToDetails.forEach((items, orderId) => {
      const summaryMatched = searchSummaryKeyword === ""
        || items.some(item => item.summary.includes(searchSummaryKeyword));
      const memoMatched = searchMemoKeyword === ""
        || items.some(item => item.memo.includes(searchMemoKeyword));
      if (summaryMatched && memoMatched) {
        matchedOrderIds.push(orderId);
      }
    });
    return matchedOrderIds;
  };

  // ECショップ用昇順に変換
  const sortByAscOrder = (details: Detail[]): Detail[] => {
    const sorted = [...details]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .sort((a, b) => a.cancelOrder - b.cancelOrder)
      .sort((a, b) => Number(a.orderId) - Number(b.orderId))
      .sort((a, b) => Number(a.date) - Number(b.date));
    return sorted;
  }

  // ECショップ用降順に変換
  const sortByDescOrder = (details: Detail[]): Detail[] => {
    const sorted = [...details]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .sort((a, b) => a.cancelOrder - b.cancelOrder)
      .sort((a, b) => Number(b.orderId) - Number(a.orderId))
      .sort((a, b) => Number(b.date) - Number(a.date));
    return sorted;
  }

  const filterDetails = (passbookType: string) => {
    if (passbookType === "20") {
      // ECショップ
      // 受注データは受注詳細(明細データ)をもとに受注ヘッダを作成し受注ID単位で管理する
      // 1受注 = 受注ヘッダ + 受注詳細(明細)

      // 日付、入出金タイプ、残高、無効明細で絞り込み
      const filteredByDateEtc = details
        // 日付範囲
        .filter(item => (searchStartDate !== "" ? item.date.replace(/-/g, "") >= searchStartDate : true))
        .filter(item => (searchEndDate !== "" ? item.date.replace(/-/g, "") <= searchEndDate : true))
        // 入出金タイプ
        .filter(item => (searchTradeType === 1 ? item.tradeType === 1 : true))
        .filter(item => (searchTradeType === 2 ? item.tradeType === 2 : true))
        // 残高
        .filter(item => (searchMinBalance !== undefined ? (item.balance ?? 0) >= searchMinBalance : true))
        .filter(item => (searchMaxBalance !== undefined ? (item.balance ?? 0) <= searchMaxBalance : true))
        // 無効明細
        //.filter(item => (activeInvalid ? true : item.valid === true));

      // 受注ヘッダの作成
      const detailsWithHeaders = addOrdersHeadersToList(filteredByDateEtc)
      // 入出金検索の対象となる受注情報を持つ受注
      const orderIdListAmount = amountMatchOrderIdList(detailsWithHeaders);
      // 摘要・メモ検索対象の受注IDリスト作成
      const orderIdListKeyword = keywordMatchOrderIdList(detailsWithHeaders);

      // 入出金金額、取引内容、メモで絞り込み
      const set1 = new Set(orderIdListAmount);
      const set2 = new Set(orderIdListKeyword);
      const filteredByOrderId = detailsWithHeaders.filter(item =>
        set1.has(item.orderId) && set2.has(item.orderId)
      );

      // 昇順・降順
      if (sortType === "asc") {
        setSelectedDetails(sortByAscOrder(filteredByOrderId));
      } else if (sortType === "desc") {
        setSelectedDetails(sortByDescOrder(filteredByOrderId));
      }
    }
    else {
      const filteredDetails = details
        // 日付範囲
        .filter(item => (searchStartDate !== "" ? item.date.replace(/-/g, "") >= searchStartDate : true))
        .filter(item => (searchEndDate !== "" ? item.date.replace(/-/g, "") <= searchEndDate : true))
        // 入出金タイプ
        .filter(item => (searchTradeType === 1 ? item.tradeType === 1 : true))
        .filter(item => (searchTradeType === 2 ? item.tradeType === 2 : true))
        // 入出金金額
        .filter(item => (searchMinAmount !== undefined ? item.amount >= searchMinAmount : true))
        .filter(item => (searchMaxAmount !== undefined ? item.amount <= searchMaxAmount : true))
        // 残高
        .filter(item => (searchMinBalance !== undefined ? (item.balance ?? 0) >= searchMinBalance : true))
        .filter(item => (searchMaxBalance !== undefined ? (item.balance ?? 0) <= searchMaxBalance : true))
        // 取引内容
        .filter(item => (searchSummaryKeyword !== "" ? item.summary.includes(searchSummaryKeyword) : true))
        // メモ
        .filter(item => (searchMemoKeyword !== "" ? item.memo.includes(searchMemoKeyword) : true))
        // 無効明細
        //.filter(item => (activeInvalid ? true : item.valid === true));

      // 昇順・降順
      if (sortType === "asc") {
        setSelectedDetails(filteredDetails.slice().reverse());
      } else if (sortType === "desc") {
        setSelectedDetails(filteredDetails);
      }
    }
  };

  // 明細更新（アグリゲーション）
  const updateTrades = async (
    accountGroupId: string,
    mlinkAccountId: string,
    mlinkId: string,
    passbookType: string,
    scrapeType: number,
    startDate?: string,
    endDate?: string,
    otpInfoOverride?: string,
  ) => {
    // 銀行API
    if (passbookType === "01"){
      const updateFinanceTradesRes = await updateFinanceTrades(
        accountGroupId,
        mlinkAccountId,
        mlinkId
      );
      if (updateFinanceTradesRes) {
        addPollingInfo({
          processId: updateFinanceTradesRes.process_id,
          accountGroupId,
          mlinkAccountId,
          mlinkId,
          passbookType,
        });
      }
    }
    // クレカAPI
    else if (passbookType === "19659" || passbookType === "19660"){
      const updateCrecaTradesRes = await updateCrecaTrades(
        accountGroupId,
        mlinkAccountId
      );
      if (updateCrecaTradesRes) {
        addPollingInfo({
          processId: updateCrecaTradesRes.process_id,
          accountGroupId,
          mlinkAccountId,
          mlinkId,
          passbookType,
        });
      }
    }
    // Look口座
    else if (passbookType === "03" || passbookType === "10"){
      // サーバ版スクレイピング
      if (scrapeType === 2) {
        // 期間が指定された場合はその期間で更新、されない場合は全期間で更新
        let updatePaymentScrapTradesRes;
        if (startDate && endDate) {
          updatePaymentScrapTradesRes = await updatePaymentScrapTrades(
            accountGroupId,
            mlinkAccountId,
            mlinkId,
            startDate,
            endDate,
            sessionKey,
            otpInfoOverride ?? otpInfo
          );
        } else {
          updatePaymentScrapTradesRes = await updatePaymentScrapTrades(
            accountGroupId,
            mlinkAccountId,
            mlinkId,
            undefined,
            undefined,
            sessionKey,
            otpInfoOverride ?? otpInfo
          );
        }
        // サーバー版スクレイピングの時のみポーリングを行う
        if (updatePaymentScrapTradesRes) {
          addPollingInfo({
            processId: updatePaymentScrapTradesRes.process_id,
            accountGroupId,
            mlinkAccountId,
            mlinkId,
            passbookType,
            scrapeType: scrapeType,
          });
        }
      }
      // API版
      else if (scrapeType === 3) {
        await updatePaymentApiTrades(
          accountGroupId,
          mlinkAccountId,
          mlinkId,
          passbookType,
          scrapeType,
        );
      }
    }
    // ECショップ
    else if (passbookType === "20"){
      await updateEcshopTrades(
        accountGroupId,
        mlinkAccountId,
        mlinkId,
        passbookType,
      );
    }
  };

  const updateDetailMemo = async (tradeId: string, newMemo: string, passbookType: string) => {
    if (!selectedAccount) {
      return false;
    }
    const targetDetail = details.find(item => item.tradeId === tradeId);
    if (targetDetail) {
      // 元のメモを保存
      const originalMemo = targetDetail.memo;
      // ローカル情報を保存しておく
      targetDetail.memo = newMemo;
      filterDetails(selectedAccount.passbookType);

      let success = false;
      if (passbookType === "01") {
        // finance
        success = await updateFinanceTradesAdditions(
          selectedAccount.accountGroupId,
          selectedAccount.mlinkAccountId,
          selectedAccount.mlinkId,
          tradeId,
          newMemo,
          undefined,
        ) === true;
      } else if (passbookType === "09") {
        // scrap-finance
        success = await updateFinanceTradesAdditions(
          selectedAccount.accountGroupId,
          selectedAccount.mlinkAccountId,
          selectedAccount.mlinkId,
          tradeId,
          newMemo,
          undefined,
        ) === true;
      } else if (passbookType === "19659" || passbookType === "19660") {
        // creca
        success = await updateCrecaTradesAdditions(
          selectedAccount.accountGroupId,
          selectedAccount.mlinkAccountId,
          tradeId,
          newMemo,
          undefined,
        ) === true;
      } else if (passbookType === "03" || passbookType === "10") {
        // payment
        success = await updatePaymentTradesAdditions(
          selectedAccount.accountGroupId,
          selectedAccount.mlinkAccountId,
          selectedAccount.mlinkId,
          tradeId,
          newMemo,
          undefined,
        ) === true;
      } else if (passbookType === "20") {
        // ecshop
        success = await updateEcshopTradesAdditions(
          selectedAccount.accountGroupId,
          selectedAccount.mlinkAccountId,
          selectedAccount.mlinkId,
          tradeId,
          newMemo,
          undefined,
        ) === true;
      } else if (passbookType === "201" || passbookType === "202") {
        // csv-excel
        success = await updateCsvExcelTradesAdditions(
          selectedAccount.accountGroupId,
          selectedAccount.mlinkAccountId,
          tradeId,
          newMemo,
          undefined,
        ) === true;
      } else if (passbookType === "101") {
        // ocr
        success = await updateOcrTradesAdditions(
          selectedAccount.mlinkAccountId,
          tradeId,
          newMemo,
          undefined,
        ) === true;
      }

      // APIエラー時は元のメモにロールバック
      if (!success) {
        targetDetail.memo = originalMemo;
        filterDetails(selectedAccount.passbookType);
      }

      return success;
    }
    return false;
  };

  const updateDetailValid = async (
    tradeId: string,
    orderId: string,
    valid: boolean,
    passbookType: string
  ) => {
    if (!selectedAccount) {
      return;
    }
    const newInvalid = valid === false ? 1 : 0;

    if (passbookType === "20") {
      const targetDetails = details.filter(item => item.orderId === orderId);
      if (targetDetails.length > 0) {
        // 元の値を保存
        const originalValues = new Map(targetDetails.map(detail => [detail.tradeId, detail.invalid]));
        // ローカル情報を更新しておく
        for (const detail of targetDetails) {
          detail.invalid = newInvalid;
        }
        filterDetails(selectedAccount.passbookType);

        // API呼び出し（失敗した明細があればそれだけロールバック）
        for (const detail of targetDetails) {
          const success = await updateEcshopTradesAdditions(
            selectedAccount.accountGroupId,
            selectedAccount.mlinkAccountId,
            selectedAccount.mlinkId,
            detail.tradeId,
            undefined,
            newInvalid,
          ) === true;
          if (!success) {
            // この明細のみロールバック
            detail.invalid = originalValues.get(detail.tradeId) ?? detail.invalid;
          }
        }
        filterDetails(selectedAccount.passbookType);
      }
    }
    else {
      const targetDetail = details.find(item => item.tradeId === tradeId);
      if (targetDetail) {
        // 元の値を保存
        const originalInvalid = targetDetail.invalid;
        // ローカル情報を更新しておく
        targetDetail.invalid = newInvalid;
        filterDetails(selectedAccount.passbookType);

        let success = false;
        if (passbookType === "01") {
          // finance
          success = await updateFinanceTradesAdditions(
            selectedAccount.accountGroupId,
            selectedAccount.mlinkAccountId,
            selectedAccount.mlinkId,
            tradeId,
            undefined,
            newInvalid,
          ) === true;
        } else if (passbookType === "19659" || passbookType === "19660") {
          // creca
          success = await updateCrecaTradesAdditions(
            selectedAccount.accountGroupId,
            selectedAccount.mlinkAccountId,
            tradeId,
            undefined,
            newInvalid,
          ) === true;
        } else if (passbookType === "03" || passbookType === "10") {
          // payment
          success = await updatePaymentTradesAdditions(
            selectedAccount.accountGroupId,
            selectedAccount.mlinkAccountId,
            selectedAccount.mlinkId,
            tradeId,
            undefined,
            newInvalid,
          ) === true;
        } else if (passbookType === "201" || passbookType === "202") {
          // csv-excel
          success = await updateCsvExcelTradesAdditions(
            selectedAccount.accountGroupId,
            selectedAccount.mlinkAccountId,
            tradeId,
            undefined,
            newInvalid,
          ) === true;
        } else if (passbookType === "101") {
          // ocr
          success = await updateOcrTradesAdditions(
            selectedAccount.mlinkAccountId,
            tradeId,
            undefined,
            newInvalid,
          ) === true;
        }

        // APIエラー時はロールバック
        if (!success) {
          targetDetail.invalid = originalInvalid;
          filterDetails(selectedAccount.passbookType);
        }
      }
    }
  };

  const getTrdaes = async (
    accountGroupId: string,
    mlinkAccountId: string,
    mlinkId: string,
    passbookType: string,
    industryCode: string,
    companyCode: string,
    startDate?: string,
    endDate?: string,
    hasEarlierMore?: boolean,
    hasLaterMore?: boolean,
  ) :Promise<Detail[]> => {
    loadingCtx.setIsLoading(true);
    try {
      if (forceTradesSetDone) return details;
      const now = new Date();
      const todayStr = toYYYYMMDD(now);
      let start;
      let end;
      // startDate、endDate両方指定
      if (startDate && endDate) {
          start = startDate < todayStr ? startDate : todayStr;
          end = endDate < todayStr ? endDate : todayStr;
          setTradesStartDate(start);
          setTradesEndDate(end);
      }
      // startDate、endDate両方指定なし
      else if (!startDate && !endDate) {
        const oneYearAgo = addYears(now, -1);
        start = toYYYYMMDD(oneYearAgo);
        end = todayStr;
        setTradesStartDate(start);
        setTradesEndDate(end);
      }
      // ③ startのみ
      else if (startDate) {
        const startDateObj = toDate(startDate);
        const oneYearLater = addYears(startDateObj, 1);
        const endDateObj = oneYearLater > now ? now : oneYearLater;
    
        start = startDate < todayStr ? startDate : todayStr;
        end = toYYYYMMDD(endDateObj);
        setTradesEndDate(end);
      }
      // ④ endのみ
      else if (endDate) {
        const endDateObj = toDate(endDate);
        const oneYearAgo = addYears(endDateObj, -1);

        start = toYYYYMMDD(oneYearAgo) < todayStr ? toYYYYMMDD(oneYearAgo) : todayStr;
        end = endDate < todayStr ? endDate : todayStr;
        setTradesStartDate(start);
      }
      else {
        throw new Error("日付指定が不正です。");
      }

      let trades: Detail[] = [];

      // finance
      if (passbookType === "01") {
        const res = await getFinanceTrades(accountGroupId, mlinkAccountId, mlinkId, industryCode, companyCode, start, end);
        if (res === undefined) {
          return [];
        }
        trades = res.map(item => ({
          mlinkAccountId: item.finance_account_id,
          mlinkId: item.finance_id,
          tradeId: item.trade_bank_id,
          date: item.date,
          sortOrder: Number(item.sort_order),
          tradeType: Number(item.trade_type),
          amount: Number(item.amount),
          balance: Number(item.balance_amount),
          summary: item.summary ?? "",
          memo: item.memo ?? "",
          invalid: item.is_invalid,
          orderId: "",
          cancelOrder: 0,
          isHeader: 0,
          isEcshopTrade: 0,
          ocrProcessId: "",
        }));
      }
      // scrap-bank
      else if (passbookType === "09") {
        const res = await getScrapFinanceTrades(accountGroupId, mlinkAccountId, mlinkId, start, end);
        if (res === undefined) {
          return [];
        }
        trades = res.map(item => ({
          mlinkAccountId: item.scrap_finance_account_id,
          mlinkId: item.scrap_finance_id,
          tradeId: item.trade_bank_id,
          date: item.date,
          sortOrder: Number(item.sort_order),
          tradeType: Number(item.trade_type),
          amount: Number(item.amount),
          balance: Number(item.balance_amount),
          summary: item.summary ?? "",
          memo: item.memo ?? "",
          invalid: item.is_invalid,
          orderId: "",
          cancelOrder: 0,
          isHeader: 0,
          isEcshopTrade: 0,
          ocrProcessId: "",
        }));
      }
      // creca
      else if (passbookType === "19659" || passbookType === "19660") {
        const res = await getCrecaTrades(accountGroupId, mlinkAccountId, start, end);
        if (res === undefined) {
          return [];
        }
        trades = res.map(item => ({
          mlinkAccountId: item.creca_account_id,
          mlinkId: item.creca_id,
          tradeId: item.trade_creca_id,
          date: item.date,
          sortOrder: Number(item.sort_order),
          tradeType: Number(item.trade_type),
          amount: Number(item.amount),
          balance: 0,
          summary: item.summary ?? "",
          memo: item.memo ?? "",
          invalid: item.is_invalid,
          orderId: "",
          cancelOrder: 0,
          isHeader: 0,
          isEcshopTrade: 0,
          ocrProcessId: "",
        }));
      }
      // payment
      else if (passbookType === "03" || passbookType === "10") {
        // 明細同期（明細更新完了が検知できない可能性もあるため毎度同期した方がいい？）
        await synchronizePaymentTrades(
          accountGroupId,
          mlinkAccountId,
          mlinkId,
          start,
          end,
        );
        // 明細取得
        const res = await getPaymentTrades(
          accountGroupId,
          mlinkAccountId,
          mlinkId,
          start,
          end,
        );
        if (res === undefined) {
          return [];
        }
        trades = res.map(item => ({
          mlinkAccountId: item.payment_account_id,
          mlinkId: item.payment_id,
          tradeId: item.trade_id,
          date: item.date,
          sortOrder: Number(item.sort_order),
          tradeType: Number(item.trade_type),
          amount: Number(item.amount),
          balance: item.balance !== "" ? Number(item.balance) : undefined,
          summary: item.summary ?? "",
          memo: item.memo ?? "",
          invalid: item.is_invalid,
          orderId: "",
          cancelOrder: 0,
          isHeader: 0,
          isEcshopTrade: 0,
          ocrProcessId: "",
        }));
      }
      // ecshop
      else if (passbookType === "20") {
        const res = await getEcshopTrades(accountGroupId, mlinkAccountId, mlinkId, start, end);
        if (res === undefined) {
          return [];
        }
        trades = res.map(item => ({
          mlinkAccountId: item.ecshop_account_id,
          mlinkId: item.ecshop_id,
          tradeId: item.trade_id,
          date: item.date,
          sortOrder: Number(item.sort_order),
          tradeType: Number(item.trade_type),
          amount: Number(item.amount),
          balance: undefined,
          summary: item.summary ?? "",
          memo: item.memo ?? "",
          invalid: item.is_invalid,
          orderId: item.order_id,
          cancelOrder: Number(item.trade_type) === 2 ? 1 : 0,
          isHeader: 0,
          isEcshopTrade: 1,
          ocrProcessId: "",
        }));
      }
      // csv-excel
      else if (passbookType === "201" || passbookType === "202") {
        const res = await getCsvExcelTrades(accountGroupId, mlinkAccountId, start, end);
        if (res === undefined) {
          return [];
        }
        trades = res.map(item => ({
          mlinkAccountId: item.csvexcel_account_id,
          mlinkId: "",
          tradeId: item.trade_id,
          date: item.date,
          sortOrder: Number(item.sort_order),
          tradeType: Number(item.trade_type),
          amount: Number(item.amount),
          balance: item.balance !== "" ? Number(item.balance) : undefined,
          summary: item.summary ?? "",
          memo: item.memo ?? "",
          invalid: item.is_invalid,
          orderId: "",
          cancelOrder: 0,
          isHeader: 0,
          isEcshopTrade: 0,
          ocrProcessId: "",
        }));
      }
      // ocr
      else if (passbookType === "101") {
        // デモ用にOCRの時だけ取得期間を1年に拡大
        // const prevYearDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        // const y = prevYearDate.getFullYear();
        // const m = String(prevYearDate.getMonth() + 1).padStart(2, "0");
        // const d = String(prevYearDate.getDate()).padStart(2, "0");
        // const start = startDate
        // startDate
        // : `${y}${m}${d}`; // 1年前の日付
        const res = await getOcrTrades(accountGroupId, mlinkAccountId, start, end);
        if (res === undefined) {
          return [];
        }
        trades = res.map(item => ({
          mlinkAccountId: item.ocr_account_id,
          mlinkId: "" ,
          tradeId: item.trade_id,
          date: item.date,
          sortOrder: Number(item.sort_order),
          tradeType: Number(item.trade_type),
          amount: Number(item.amount),
          balance: Number(item.balance_amount),
          summary: item.summary ?? "",
          memo: item.memo ?? "",
          invalid: item.is_invalid,
          orderId: "",
          cancelOrder: 0,
          isHeader: 0,
          isEcshopTrade: 0,
          ocrProcessId: item.ocr_process_id,
        }));
      }

      // 開始日指定の場合は既存明細の開始日と同じ日付の明細を削除し、そこに新規で取ってきた明細を加える
      // これにより既存明細の最終日明細と同一日付の明細の更新に対応
      let setTrades = details;
      if (startDate) {
        setTrades = setTrades.filter(item => item.date.replace(/-/g, "") !== startDate);
      }
      if (hasEarlierMore) {
        setDetails([...setTrades, ...trades]); // 降順のため古い明細を後ろに追加
      } else if (hasLaterMore) {
        setDetails([...trades, ...setTrades]); // 降順のため新しい明細を前に追加
      } else {
        setDetails(trades);
      }

      return trades;
    } catch (error) {
      console.log("getTrdaes", error);
      return [];
    } finally {
      loadingCtx.setIsLoading(false);
    }
  }

  const getTradesHistory = async (
    accountGroupId: string,
    mlinkAccountId: string,
    mlinkId: string,
    passbookType: string,
    offset?: string,
  ): Promise<TradesHistory> => {
    loadingCtx.setIsLoading(true);
    try {
      const tradesHistoryInit: TradesHistory = {
        mlinkAccountId: "",
        mlinkId: "",
        data: [],
        hasMore: false
      };
      let tradesHistory: TradesHistory = tradesHistoryInit;

      // finance
      if (passbookType === "01") {
        const res = await getFinanceTradesHistory(accountGroupId, mlinkAccountId, mlinkId, offset);
        if (res === undefined) {
          return tradesHistoryInit;
        }
        const historyData = res.data.map(item => ({
          executedAt: item.executed_at,
          updateType: item.update_type,
          status: item.status,
          user: item.user,
          details: item.details
        }));
        tradesHistory = {
          mlinkAccountId: res.finance_account_id,
          mlinkId: res.finance_id,
          data: historyData,
          hasMore: res.has_more ?? false
        };
      }
      else if (passbookType === "09") {
        const res = await getScrapFinanceTradesHistory(accountGroupId, mlinkAccountId, mlinkId, offset);
        if (res === undefined) {
          return tradesHistoryInit;
        }
        const historyData = res.data.map(item => ({
          executedAt: item.executed_at,
          updateType: item.update_type,
          status: item.status,
          user: item.user,
          details: item.details
        }));
        tradesHistory = {
          mlinkAccountId: res.scrap_finance_account_id,
          mlinkId: res.scrap_finance_id,
          data: historyData,
          hasMore: res.has_more ?? false
        };
      }
      // creca
      else if (passbookType === "19659" || passbookType === "19660") {
        const res = await getCrecaTradesHistory(accountGroupId, mlinkAccountId, offset);
        if (res === undefined) {
          return tradesHistoryInit;
        }
        const historyData = res.data.map(item => ({
          executedAt: item.executed_at,
          updateType: item.update_type,
          status: item.status,
          user: item.user,
          details: item.details
        }));
        tradesHistory = {
          mlinkAccountId: res.creca_account_id,
          mlinkId: "", // creca_idが無いため空白
          data: historyData,
          hasMore: res.has_more ?? false
        };
      }
      // payment
      else if (passbookType === "03" || passbookType === "10") {
        const res = await getPaymentTradesHistory(accountGroupId, mlinkAccountId, mlinkId, offset);
        if (res === undefined) {
          return tradesHistoryInit;
        }
        const historyData = res.data.map(item => ({
          executedAt: item.executed_at,
          updateType: item.update_type,
          status: item.status,
          user: item.user,
          details: item.details
        }));
        tradesHistory = {
          mlinkAccountId: res.payment_account_id,
          mlinkId: res.payment_id,
          data: historyData,
          hasMore: res.has_more ?? false
        };
      }
      // ecshop
      else if (passbookType === "20") {
        const res = await getEcshopTradesHistory(accountGroupId, mlinkAccountId, mlinkId, offset);
        if (res === undefined) {
          return tradesHistoryInit;
        }
        const historyData = res.data.map(item => ({
          executedAt: item.executed_at,
          updateType: item.update_type,
          status: item.status,
          user: item.user,
          details: item.details
        }));
        tradesHistory = {
          mlinkAccountId: res.ecshop_account_id,
          mlinkId: res.ecshop_id,
          data: historyData,
          hasMore: res.has_more ?? false
        };
      }
      // csv-excel
      else if (passbookType === "201" || passbookType === "202") {
          const res = await getCsvExcelTradesHistory(accountGroupId, mlinkAccountId, offset);
          if (res === undefined) {
              return tradesHistoryInit;
          }
          const historyData = res.data.map((item) => ({
              executedAt: item.executed_at,
              updateType: item.update_type,
              status: item.status,
              user: item.user,
              details: item.details,
          }));
          tradesHistory = {
              mlinkAccountId: res.csvexcel_account_id,
              mlinkId: "", // csvexcel_idが無いため空白
              data: historyData,
              hasMore: res.has_more ?? false
          };
      }
      // ocr
      else if (passbookType === "101") {
          const res = await getOcrTradesHistory(accountGroupId, mlinkAccountId, offset);
          if (res === undefined) {
              return tradesHistoryInit;
          }
          const historyData = res.data.map((item) => ({
              executedAt: item.executed_at,
              updateType: item.update_type,
              status: item.status,
              user: item.user,
              details: item.details,
          }));
          tradesHistory = {
              mlinkAccountId: res.ocr_account_id,
              mlinkId: "", // ocr_idが無いため空白
              data: historyData,
              hasMore: res.has_more ?? false
          };
      }
      return tradesHistory;
    } catch (error) {
      console.log("getTrdaesHistory", error);
      return {
        mlinkAccountId: "",
        mlinkId: "",
        data: [],
        hasMore: false
      };
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  return {
    filterDetails: errorHandledSync(filterDetails, setError),
    updateTrades: errorHandledAsync(updateTrades, setError),
    updateDetailMemo: errorHandledAsync(updateDetailMemo, setError),
    updateDetailValid: errorHandledAsync(updateDetailValid, setError),
    getTrdaes: errorHandledAsync(getTrdaes, setError),
    getTradesHistory: errorHandledAsync(getTradesHistory, setError),
  };
};
